import React, { useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

export const Combobox = ({
  value = "",
  onValueChange = () => {},
  placeholder = "Select or type...",
  searchPlaceholder = "Search or type new...",
  optionsLabel = "",
  options = [], // Array of {value, label, group?} objects
  onDelete = null, // Function to delete an option
  createLabel = "Create new",
  emptyLabel = "No results found",
  disabled = false,
  className = "",
  is_create = true,
  testId = undefined
}) => {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [triggerWidth, setTriggerWidth] = useState(0);
  const triggerRef = React.useRef(null);

  // Update trigger width when popover opens
  React.useEffect(() => {
    if (open && triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, [open]);

  // Filter options based on search
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchValue.toLowerCase())
  );

  // Preserve the caller's option order while allowing related choices to be
  // presented under optional headings. Existing ungrouped callers still get
  // one group headed by optionsLabel, exactly as before.
  const hasOptionGroups = options.some(option => String(option.group || '').trim());
  const optionGroups = hasOptionGroups
    ? filteredOptions.reduce((groups, option) => {
        const heading = String(option.group || '').trim();
        const key = heading || '__ungrouped__';
        let group = groups.find(item => item.key === key);
        if (!group) {
          group = { key, heading: heading || optionsLabel || undefined, options: [] };
          groups.push(group);
        }
        group.options.push(option);
        return groups;
      }, [])
    : [{ key: '__default__', heading: optionsLabel || undefined, options: filteredOptions }];

  // Check if current search would create a new option
  const isNewOption = searchValue.trim() && 
    !options.some(option => option.value.toLowerCase() === searchValue.toLowerCase());

  const handleSelect = (selectedValue) => {
    console.log('🔥 Combobox: Selected value:', selectedValue);
    onValueChange(selectedValue);
    setOpen(false);
    setSearchValue("");
  };

  const handleCreateNew = () => {
    const newValue = searchValue.trim();
    if (newValue) {
      console.log('🔥 Combobox: Creating new value:', newValue);
      onValueChange(newValue);
      setOpen(false);
      setSearchValue("");
    }
  };

  // Handle Enter key press for creation
  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && isNewOption) {
      event.preventDefault();
      handleCreateNew();
    }
  };

  const handleDelete = async (optionValue, event) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (onDelete) {
      try {
        console.log('🗑️ Combobox: Deleting option:', optionValue);
        await onDelete(optionValue);
        
        // If the deleted option was selected, clear the selection
        if (value === optionValue) {
          onValueChange("");
        }
      } catch (error) {
        console.error('❌ Combobox: Error deleting option:', error);
        alert(`Failed to delete "${optionValue}". ${error.message}`);
      }
    }
  };

  // Get display value
  const selectedOption = options.find(option => option.value === value);
  const displayValue = selectedOption ? selectedOption.label : value;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className={cn(
            "w-full justify-between text-left font-normal",
            !value && "text-muted-foreground",
            disabled && "cursor-not-allowed opacity-50",
            className
          )}
          disabled={disabled}
        >
          <span className="truncate">
            {value ? displayValue : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="p-0" 
        align="start"
        sideOffset={4}
        style={{ width: triggerWidth || 'auto', minWidth: triggerWidth || 'auto' }}
      >
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder={searchPlaceholder}
            value={searchValue}
            onValueChange={setSearchValue}
            onKeyDown={handleKeyDown}
            className="h-9"
          />
          <CommandList className="max-h-64">
            <CommandEmpty>
              <div className="p-2 text-center text-sm text-muted-foreground">
                {emptyLabel}
              </div>
              {(isNewOption && is_create) && (
                <Button
                  variant="ghost"
                  className="w-full justify-start"
                  onClick={handleCreateNew}
                >
                  <Check className="mr-2 h-4 w-4" />
                  {createLabel}: "{searchValue}"
                </Button>
              )}
            </CommandEmpty>

            {/* Show create option at the top, before any grouped results. */}
            {(isNewOption && is_create) && (
              <CommandGroup>
                <CommandItem onSelect={handleCreateNew}>
                  <Check className="mr-2 h-4 w-4" />
                  {createLabel}: "{searchValue}"
                </CommandItem>
              </CommandGroup>
            )}

            {optionGroups.map((group) => (
              <CommandGroup key={group.key} heading={group.heading}>
                {group.options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={() => handleSelect(option.value)}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center">
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === option.value ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <span className="truncate">{option.label}</span>
                    </div>

                    {/* Delete button */}
                    {onDelete && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-2 h-6 w-6 p-0 opacity-50 hover:opacity-100 hover:bg-red-100"
                        onClick={(e) => handleDelete(option.value, e)}
                        title={`Delete ${option.label}`}
                      >
                        <X className="h-3 w-3 text-red-500" />
                      </Button>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default Combobox;
